import type { HallBoothCollection } from "../types";
import {
  findBoothNodeId,
  nearestNodeId,
  shortestWalk,
  type CadPoint,
  type WalkGraph,
} from "./aisleRoute";
import { featureCentroid } from "./gpsCadOffset";

export const DEFAULT_HALL_ORDER = ["A", "D", "E", "C"] as const;

export type TourStop = {
  id: string;
  booth: string;
  hall: string;
  x: number;
  y: number;
};

export type TourOrigin = {
  booth?: string;
  hall?: string;
  x: number;
  y: number;
};

export function hallKey(raw: string | null | undefined, fallback = ""): string {
  const normalized = (raw ?? "").replace(/^hall\s+/i, "").trim().toUpperCase();
  if (normalized) return normalized;
  return fallback.replace(/^hall\s+/i, "").trim().toUpperCase();
}

export function boothCentroidsFromHall(booths: HallBoothCollection | null | undefined): Map<string, CadPoint> {
  const map = new Map<string, CadPoint>();
  for (const feature of booths?.features ?? []) {
    const id = feature.properties?.booth_id?.trim();
    if (!id) continue;
    const geom = feature.geometry;
    if (!geom || (geom.type !== "Polygon" && geom.type !== "MultiPolygon")) continue;
    const centroid = featureCentroid(geom);
    if (!centroid) continue;
    map.set(id, centroid);
    map.set(id.toLowerCase(), centroid);
  }
  return map;
}

export function centroidForBooth(centroids: Map<string, CadPoint>, booth: string): CadPoint | null {
  const key = booth.trim();
  if (!key) return null;
  return centroids.get(key) ?? centroids.get(key.toLowerCase()) ?? null;
}

export function resolveHallOrder(halls: Iterable<string>, currentHall?: string | null): string[] {
  const unique = [...new Set([...halls].map((hall) => hallKey(hall)).filter(Boolean))];
  const current = hallKey(currentHall);
  const knownFirst = (keys: string[]) => {
    const ranked = DEFAULT_HALL_ORDER.filter((hall) => keys.includes(hall));
    const other = keys.filter((hall) => !DEFAULT_HALL_ORDER.includes(hall as (typeof DEFAULT_HALL_ORDER)[number])).sort();
    return [...ranked, ...other];
  };
  if (current && unique.includes(current)) {
    return [current, ...knownFirst(unique.filter((hall) => hall !== current))];
  }
  return knownFirst(unique);
}

function hypot(a: CadPoint, b: CadPoint): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

function pathLength(stops: readonly TourStop[], origin?: CadPoint | null): number {
  let total = 0;
  let prev = origin ?? null;
  for (const stop of stops) {
    if (prev) total += hypot(prev, stop);
    prev = stop;
  }
  return total;
}

export function nearestNeighbor(stops: TourStop[], origin?: CadPoint | null): TourStop[] {
  if (!stops.length) return [];
  const remaining = stops.slice();
  const path: TourStop[] = [];
  let current: CadPoint | null = origin ?? null;
  if (!current) {
    const first = remaining.shift();
    if (!first) return [];
    path.push(first);
    current = first;
  }
  while (remaining.length) {
    let bestAt = 0;
    let bestD = Infinity;
    for (let i = 0; i < remaining.length; i += 1) {
      const d = hypot(current, remaining[i]!);
      if (d < bestD) {
        bestD = d;
        bestAt = i;
      }
    }
    const next = remaining.splice(bestAt, 1)[0]!;
    path.push(next);
    current = next;
  }
  return path;
}

export function twoOpt(stops: TourStop[], origin?: CadPoint | null, maxPasses = 200): TourStop[] {
  if (stops.length < 3) return stops.slice();
  let path = stops.slice();
  let improved = true;
  let guard = 0;
  while (improved && guard < maxPasses) {
    improved = false;
    guard += 1;
    const baseline = pathLength(path, origin);
    for (let i = 0; i < path.length - 1; i += 1) {
      for (let k = i + 1; k < path.length; k += 1) {
        const next = path.slice(0, i).concat(path.slice(i, k + 1).reverse(), path.slice(k + 1));
        if (pathLength(next, origin) + 1e-9 < baseline) {
          path = next;
          improved = true;
          break;
        }
      }
      if (improved) break;
    }
  }
  return path;
}

export function buildEuclideanTour(
  stops: TourStop[],
  options?: {
    origin?: TourOrigin | null;
    currentHall?: string | null;
    use2Opt?: boolean;
  },
): TourStop[] {
  if (!stops.length) return [];
  const use2Opt = options?.use2Opt !== false;
  const origin = options?.origin ?? null;
  const byHall = new Map<string, TourStop[]>();
  for (const stop of stops) {
    const key = hallKey(stop.hall, options?.currentHall ?? "");
    const list = byHall.get(key);
    if (list) list.push(stop);
    else byHall.set(key, [stop]);
  }
  const sequence = resolveHallOrder(byHall.keys(), options?.currentHall);
  const ordered: TourStop[] = [];
  for (const hall of sequence) {
    const group = byHall.get(hall);
    if (!group?.length) continue;
    const originHere =
      origin && (hallKey(origin.hall) === hall || (!origin.hall && hallKey(options?.currentHall) === hall))
        ? origin
        : null;
    let path = nearestNeighbor(group, originHere);
    if (use2Opt) path = twoOpt(path, originHere);
    ordered.push(...path);
  }
  return ordered;
}

function appendLeg(points: CadPoint[], leg: CadPoint[]): void {
  for (const point of leg) {
    const last = points[points.length - 1];
    if (last && last.x === point.x && last.y === point.y) continue;
    points.push(point);
  }
}

export function stitchTourPath(args: {
  booths: string[];
  centroids: Map<string, CadPoint>;
  graph?: WalkGraph | null;
  originBooth?: string | null;
  originPoint?: CadPoint | null;
}): CadPoint[] {
  const { booths, centroids, graph = null, originBooth = null, originPoint = null } = args;
  const originKey = originBooth?.trim().toLowerCase() ?? "";
  const firstBooth = booths[0]?.trim().toLowerCase() ?? "";
  const originIsFirst = Boolean(originKey && firstBooth === originKey);

  const nodeFor = (booth: string | null | undefined, point: CadPoint | null): string | null => {
    if (!graph) return null;
    if (booth) {
      const id = findBoothNodeId(graph, booth);
      if (id) return id;
    }
    if (point) return nearestNodeId(graph, point.x, point.y);
    return null;
  };
  const pointForNode = (id: string | null): CadPoint | null => {
    if (!graph || !id) return null;
    const node = graph.byId.get(id);
    return node ? { x: node.x, y: node.y } : null;
  };

  const originCentroid = originBooth ? centroidForBooth(centroids, originBooth) : null;
  let prevNode = nodeFor(originBooth, originPoint ?? originCentroid);
  let prevPoint = originPoint ?? pointForNode(prevNode) ?? originCentroid;
  const remaining = originIsFirst ? booths.slice(1) : booths.slice();

  const points: CadPoint[] = [];
  if (!prevPoint && remaining.length) {
    const first = remaining.shift()!;
    prevNode = nodeFor(first, centroidForBooth(centroids, first));
    prevPoint = pointForNode(prevNode) ?? centroidForBooth(centroids, first);
  }
  if (prevPoint) appendLeg(points, [prevPoint]);

  for (const booth of remaining) {
    const goalPoint = centroidForBooth(centroids, booth);
    const goalNode = nodeFor(booth, goalPoint);
    if (graph && prevNode && goalNode) {
      const walk = shortestWalk(graph, prevNode, goalNode);
      if (walk?.length) {
        appendLeg(points, walk);
        prevNode = goalNode;
        prevPoint = walk[walk.length - 1] ?? prevPoint;
        continue;
      }
    }
    if (goalPoint) appendLeg(points, [goalPoint]);
    prevPoint = goalPoint ?? prevPoint;
    prevNode = goalNode ?? prevNode;
  }
  return points;
}

export function firstSwitchHall(
  halls: Array<string | null | undefined>,
  currentHall: string,
  allowed: readonly string[] = ["A", "C"],
): string | null {
  const current = hallKey(currentHall);
  for (const raw of halls) {
    const next = hallKey(raw);
    if (next && next !== current && allowed.includes(next)) return next;
  }
  return null;
}
